import {
  collection,
  getDocs,
  query,
  where
} from "firebase/firestore";

import { db } from "../firebase";

/* =================================================
   DETERMINISTIC SORTER
================================================= */

const sortTransactions = (
  docs = []
) => {

  return [...docs].sort((a, b) => {

    const aData =
      a.data();

    const bData =
      b.data();

    // =========================================
    // PRIMARY SORT
    // created_at seconds ASC
    // =========================================

    const aSeconds =
      aData?.created_at?.seconds || 0;

    const bSeconds =
      bData?.created_at?.seconds || 0;

    if (aSeconds !== bSeconds) {

      return aSeconds - bSeconds;
    }

    // =========================================
    // SECONDARY SORT
    // nanoseconds ASC
    // =========================================

    const aNano =
      aData?.created_at?.nanoseconds || 0;

    const bNano =
      bData?.created_at?.nanoseconds || 0;

    if (aNano !== bNano) {

      return aNano - bNano;
    }

    // =========================================
    // TERTIARY SORT
    // movement_type ASC
    // =========================================

    const aMovement =
      String(
        aData?.movement_type || ""
      ).trim();

    const bMovement =
      String(
        bData?.movement_type || ""
      ).trim();

    if (aMovement !== bMovement) {

      return aMovement.localeCompare(
        bMovement
      );
    }

    // =========================================
    // FINAL TIE BREAKER
    // document id ASC
    // =========================================

    return a.id.localeCompare(
      b.id
    );
  });
};

/* =================================================
   LOOKUPS FOR THE INVENTORY LEDGER PAGE (P4-8)
   Current item codes + PO numbers, for display and
   search only. A failed read gives an empty list:
   the page still works, with the row's code / doc id.
================================================= */

export const getLedgerLookups =
  async (
    companyId
  ) => {

    const [itemsRead, poRead] =
      await Promise.allSettled([

        getDocs(query(
          collection(db, "items"),
          where("company_id", "in", [companyId, ""])
        )),

        getDocs(query(
          collection(db, "purchase_orders"),
          where("company_id", "==", companyId)
        ))
      ]);

    if (itemsRead.status === "rejected") {
      console.warn("Ledger lookup: items not read", itemsRead.reason);
    }

    if (poRead.status === "rejected") {
      console.warn("Ledger lookup: purchase orders not read", poRead.reason);
    }

    const items =
      itemsRead.status === "fulfilled"
        ? itemsRead.value.docs.map((d) => ({ id: d.id, ...d.data() }))
        : [];

    const poNumbers = new Map();

    if (poRead.status === "fulfilled") {
      poRead.value.docs.forEach((d) => {
        const number = d.data().po_number;
        if (number) poNumbers.set(d.id, number);
      });
    }

    return { items, poNumbers };
  };

/* =================================================
   INVENTORY LEDGER SERVICE
================================================= */

export const getInventoryLedger =
  async (
    companyId
  ) => {

    try {

      // =========================================
      // VALIDATION
      // =========================================

      if (!companyId) {

        throw new Error(
          "companyId required"
        );
      }

      // =========================================
      // FETCH TRANSACTIONS
      // =========================================

      let snap;

      // =========================================
      // TRY COMPANY FILTER FIRST
      // =========================================

      const companyQuery =
        query(

          collection(
            db,
            "inventory_transactions"
          ),

          where(
            "company_id",
            "==",
            companyId
          )
        );

      snap =
        await getDocs(
          companyQuery
        );

      // =========================================
      // EMPTY RESPONSE
      // company_id filter is mandatory — no legacy fallback
      // new company with no transactions = valid empty state
      // =========================================

      if (snap.empty) {

        return [];
      }

      // =========================================
      // DETERMINISTIC SORT
      // oldest -> newest
      // =========================================

      const sortedDocs =
        sortTransactions(
          snap.docs
        );

      // =========================================
      // NORMALIZED RESPONSE
      // =========================================

      const data =
        sortedDocs.map((docSnap) => {

          const d =
            docSnap.data();

          return {

            id:
              docSnap.id,

            // =====================================
            // RAW DATA
            // =====================================

            ...d,

            // =====================================
            // NORMALIZED FIELDS
            // =====================================

            qty:
              Number(
                d.qty || 0
              ),

            stock_effect:
              Number(
                d.stock_effect || 0
              ),

            stock_before:
              Number(
                d.stock_before || 0
              ),

            stock_after:
              Number(
                d.stock_after || 0
              ),

            movement_type:
              d.movement_type || "",

            item_id:
              d.item_id || "",

            item_name:
              d.item_name || "",

            item_code:
              d.item_code || "",

            category:
              d.category || "",

            unit:
              d.unit || "",

            site_id:
              d.site_id || "",

            site_name:
              d.site_name || "",

            project_id:
              d.project_id || "",

            project_name:
              d.project_name || "",

            location_type:
              d.location_type || "",

            location_id:
              d.location_id || "",

            technician_id:
              d.technician_id || "",

            technician_name:
              d.technician_name || "",

            issued_by:
              d.issued_by_name || d.issued_by || "",

            received_by:
              d.received_by_name || d.received_by || "",

            approved_by:
              d.approved_by || "",

            remarks:
              d.remarks || "",

            reference_id:
              d.reference_id || "",

            reference_type:
              d.reference_type || "",

            deterministic_key:
              d.deterministic_key || "",

            created_at:
              d.created_at || null
          };
        });

      // =========================================
      // UI DISPLAY ONLY
      // newest -> oldest
      // =========================================

      return [...data]
        .reverse();

    } catch (error) {

      console.error(
        "Inventory ledger fetch failed:",
        error
      );

      throw error;
    }
  };